import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Facebook, Linkedin } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { articles } from '../data/articles';

const ArticlesSection = () => {
  return (
    <section id="articles" className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-block bg-blue-50 text-[#3B4BA0] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Insights & Articles
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Latest from Kalah K. Singam
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Thoughts on early literacy, child development, and effective teaching
            practices — drawn from 20+ years in early childhood education.
          </p>
        </div>

        {/* Articles Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {articles.map((article) => (
            <Card
              key={article.slug}
              data-testid={`article-card-${article.slug}`}
              className="overflow-hidden border-2 border-gray-100 hover:border-[#3B4BA0] transition-all duration-300 hover:shadow-xl group bg-white flex flex-col"
            >
              {/* Hero image */}
              <Link to={`/articles/${article.slug}`} className="block">
                <div className="aspect-[16/10] overflow-hidden bg-slate-100">
                  <img
                    src={article.heroImage}
                    alt={article.heroAlt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </Link>

              <div className="p-6 flex flex-col flex-grow">
                {/* Date & read time */}
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5" />
                    {article.dateDisplay}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {article.readMinutes} min read
                  </span>
                </div>

                {/* Title */}
                <Link to={`/articles/${article.slug}`}>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#3B4BA0] transition-colors leading-snug">
                    {article.title}
                  </h3>
                </Link>

                {/* Excerpt */}
                <p className="text-gray-600 leading-relaxed mb-5 flex-grow line-clamp-3">
                  {article.excerpt}
                </p>

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-5">
                  {article.tags.map((tag, idx) => (
                    <span
                      key={idx}
                      className="text-xs bg-blue-50 text-[#3B4BA0] px-2.5 py-1 rounded-full font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Read More link */}
                <Link
                  to={`/articles/${article.slug}`}
                  data-testid={`read-article-${article.slug}`}
                  className="inline-flex items-center text-[#3B4BA0] font-semibold hover:gap-3 gap-2 transition-all"
                >
                  Read article
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Card>
          ))}
        </div>

        {/* Social Links */}
        <div className="text-center">
          <p className="text-gray-600 mb-6">
            Follow Kalah for more insights on early literacy and education
          </p>
          <div className="flex justify-center gap-4">
            <Button
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white"
              onClick={() =>
                window.open(
                  'https://www.facebook.com/learneasyphonics/',
                  '_blank'
                )
              }
            >
              <Facebook className="mr-2 h-5 w-5" />
              Facebook
            </Button>
            <Button
              variant="outline"
              className="border-2 border-blue-700 text-blue-700 hover:bg-blue-700 hover:text-white"
              onClick={() =>
                window.open(
                  'https://www.linkedin.com/in/k-kalah-kulasingam-b1749569/',
                  '_blank'
                )
              }
            >
              <Linkedin className="mr-2 h-5 w-5" />
              LinkedIn
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ArticlesSection;
